import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  HiShieldCheck,
  HiUserGroup,
  HiTruck,
  HiDocumentText,
  HiCalculator,
  HiBell,
  HiShoppingBag,
  HiLocationMarker,
  HiBriefcase,
  HiCursorClick,
  HiPhotograph,
  HiDeviceMobile,
  HiSpeakerphone,
  HiOfficeBuilding,
  HiChartBar,
  HiFlag,
  HiMail,
  HiGlobeAlt,
  FaWhatsapp
} from '../components/Icons';
import PublicNavbar from '../components/PublicNavbar';
import './Home.css';

const PUBLIC = process.env.PUBLIC_URL || '';
const STORE_BADGES = `${PUBLIC}/store-badges`;
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.boilingcode.tappytray';
const APP_STORE_URL = 'https://apps.apple.com/in/app/tappytray/id6759736366';

type StoreBadgeSize = 'default' | 'lg' | 'compact';

function StoreBadgeLinks({
  size = 'default',
  className = '',
}: {
  size?: StoreBadgeSize;
  className?: string;
}) {
  const imgClass =
    size === 'lg'
      ? 'store-badge-img store-badge-img--lg'
      : size === 'compact'
        ? 'store-badge-img store-badge-img--compact'
        : 'store-badge-img';
  return (
    <div className={`store-badge-row ${className}`.trim()}>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="store-badge-link"
      >
        <img
          src={`${STORE_BADGES}/google-play.svg`}
          alt="Get it on Google Play"
          className={imgClass}
          loading="lazy"
        />
      </a>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="store-badge-link"
      >
        <img
          src={`${STORE_BADGES}/app-store.svg`}
          alt="Download on the App Store"
          className={imgClass}
          loading="lazy"
        />
      </a>
    </div>
  );
}

const ADMIN_CLICK_COUNT = 5;
const ADMIN_CLICK_RESET_MS = 2000;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [logoClickCount, setLogoClickCount] = useState(0);
  const logoClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [offsetY, setOffsetY] = useState(0);
  const [activeAdvantage, setActiveAdvantage] = useState(0);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [howItWorksTab, setHowItWorksTab] = useState<'residents' | 'businesses'>('residents');

  const advantages = [
    {
      title: "Direct Digital Reach",
      desc: "Reach residents directly on their phones. Why waste paper on pamphlets that get thrown away?",
      icon: <HiCursorClick size={40} />
    },
    {
      title: "Society-Level Targeting",
      desc: "Pick specific societies to run your ads. Perfect for hyper-local marketing.",
      icon: <HiOfficeBuilding size={40} />
    },
    {
      title: "Real-time Dashboard",
      desc: "Track how many people saw your offer and clicked on it. Real data for real growth.",
      icon: <HiChartBar size={40} />
    },
    {
      title: "Premium Visibility",
      desc: "Get featured in the TappyTray 'Spotlight' section for maximum brand impact.",
      icon: <HiSpeakerphone size={40} />
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveAdvantage((prev) => (prev + 1) % advantages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [advantages.length]);


  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      revealElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const next = logoClickCount + 1;
    if (logoClickTimeoutRef.current) {
      clearTimeout(logoClickTimeoutRef.current);
    }
    if (next >= ADMIN_CLICK_COUNT) {
      setLogoClickCount(0);
      logoClickTimeoutRef.current = null;
      navigate('/admin/login');
      return;
    }
    setLogoClickCount(next);
    logoClickTimeoutRef.current = setTimeout(() => {
      setLogoClickCount(0);
      logoClickTimeoutRef.current = null;
    }, ADMIN_CLICK_RESET_MS);
  };

  return (
    <div className="home">
      {/* Background Decorations */}
      <div className="bg-decorations">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </div>

      <PublicNavbar onLogoClick={handleLogoClick}>
        <Link to="/society/login" className="nav-btn nav-btn-society">Society Login</Link>
        <Link to="/customer/login" className="nav-btn nav-btn-customer">Promoter Login</Link>
      </PublicNavbar>

      <main className="main">
        {/* Hero – Promotions + Society + App */}
        <section className="hero reveal">
          <div className="hero-inner-centered" style={{ transform: `translateY(${offsetY * 0.4}px)`, willChange: 'transform' }}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/hero-new-v3.png`}
              alt="Run Your Society Smarter. Grow Your Community."
              className="hero-main-image"
              decoding="async"
              style={{ marginBottom: 0 }}
            />
          </div>
        </section>

        {/* Download CTA Section (Scrolls over the parallax hero) */}
        <section 
          className="section section-download-cta reveal" 
          style={{ 
            position: 'relative', 
            zIndex: 10, 
            background: 'var(--tt-surface-2)', 
            paddingTop: '60px', 
            paddingBottom: '80px', 
            textAlign: 'center' 
          }}
        >
          <img 
            src={`${process.env.PUBLIC_URL || ''}/download-cta.png`} 
            alt="Download the Tappytray today" 
            className="download-cta-image" 
            decoding="async"
          />
          <div className="container">
            <div className="hero-ctas-centered">
              <StoreBadgeLinks size="lg" />
            </div>
          </div>
        </section>        {/* For Society Residents */}
        <section className="section section-society-residents reveal">
          <div className="container">
            <p className="section-badge">For Society Residents</p>
            <h2 className="section-title">Everything your society needs in one app</h2>
            <p className="section-desc">
              Experience seamless living with powerful management tools and exclusive local access right at your fingertips.
            </p>
            
            <div className="features-glass-grid">
              {/* Gate & Security */}
              <div className="glass-card">
                <div className="card-icon"><HiShieldCheck size={32} /></div>
                <h3>Gate & Guard Management</h3>
                <p>Ensure foolproof security with smart guard patrols and streamlined visitor entry logs.</p>
              </div>
              <div className="glass-card">
                <div className="card-icon"><HiUserGroup size={32} /></div>
                <h3>Visitor Management</h3>
                <p>Pre-approve guests, track deliveries, and keep your society premises secure at all times.</p>
              </div>
              <div className="glass-card">
                <div className="card-icon"><HiTruck size={32} /></div>
                <h3>Vehicle LookUp</h3>
                <p>Instantly search and identify vehicles parked within the society premises.</p>
              </div>
              <div className="glass-card">
                <div className="card-icon"><HiSpeakerphone size={32} /></div>
                <h3>Circulate Announcements</h3>
                <p>Stay updated with instant digital notices and important community broadcasts.</p>
              </div>
              {/* Billing & Finance */}
              <div className="glass-card">
                <div className="card-icon"><HiDocumentText size={32} /></div>
                <h3>Automated Maintenance Bills</h3>
                <p>Generate, track, and pay your monthly maintenance bills without the manual hassle.</p>
              </div>
              <div className="glass-card">
                <div className="card-icon"><HiCalculator size={32} /></div>
                <h3>Reconciliation & Accounts</h3>
                <p>Transparent accounting and instant bank reconciliation for society funds.</p>
              </div>
              <div className="glass-card">
                <div className="card-icon"><HiBell size={32} /></div>
                <h3>Bill Reminders</h3>
                <p>Never miss a due date with automated alerts for upcoming payments.</p>
              </div>
              {/* Marketplace */}
              <div className="glass-card card-highlight">
                <div className="card-icon"><HiShoppingBag size={32} /></div>
                <h3>Deals & Marketplace</h3>
                <p>Access exclusive deals nearby and discover local services and products directly in the app.</p>
              </div>
            </div>

            <div className="society-cta">
              <p className="society-cta-text">
                Committee member? Manage bills, reconciliation, and society records from the web portal.
              </p>
              <Link to="/society/login" className="btn btn-primary btn-large">
                <HiOfficeBuilding size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Society Admin Login
              </Link>
            </div>
          </div>
        </section>

        {/* For Business */}
        <section className="section section-business reveal">
          <div className="container business-container">
            <div className="business-content">
              <p className="section-badge badge-accent">For Business</p>
              <h2 className="section-title">Why use a pamphlet when you can promote digitally?</h2>
              <p className="section-desc">
                List your business and share offers or promotions directly with society residents. 
                Target high-intent local communities without the clutter of physical paper.
              </p>
              <ul className="business-benefits">
                <li><HiBriefcase className="benefit-icon"/> List your business locally</li>
                <li><HiCursorClick className="benefit-icon"/> Share instant digital promotions</li>
                <li><HiPhotograph className="benefit-icon"/> Beautiful ad layouts on mobile</li>
              </ul>
              <div className="business-cta">
                <Link to="/customer/register" className="btn btn-accent btn-large">List Your Business</Link>
                <Link to="/customer/login" className="btn btn-outline btn-large">Business Login</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Business Advantages Carousel */}
        <section className="section section-business-advantages reveal">
          <div className="container">
            <h2 className="section-title" style={{ textAlign: 'center' }}>The Digital Advantage</h2>
            <p className="section-desc" style={{ textAlign: 'center', margin: '0 auto 64px auto' }}>
              Transform your business with tools designed for the modern neighborhood.
            </p>
            
            <div className="carousel-3d-wrapper">
              <div className="carousel-3d">
                {advantages.map((adv, index) => {
                  let position = "hidden";
                  if (index === activeAdvantage) position = "active";
                  else if (index === (activeAdvantage - 1 + advantages.length) % advantages.length) position = "prev";
                  else if (index === (activeAdvantage + 1) % advantages.length) position = "next";
                  
                  return (
                    <div key={index} className={`carousel-card ${position}`}>
                      <div className="card-icon-large">{adv.icon}</div>
                      <h3>{adv.title}</h3>
                      <p>{adv.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="carousel-dots">
              {advantages.map((_, i) => (
                <div 
                  key={i} 
                  className={`dot ${i === activeAdvantage ? 'active' : ''}`}
                  onClick={() => setActiveAdvantage(i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="section section-how-it-works reveal">
          <div className="container">
            <div className="section-header-center">
              <p className="section-badge">Simple & Fast</p>
              <h2 className="section-title">How it works</h2>
              
              <div className="tab-switcher">
                <button 
                  className={`tab-btn ${howItWorksTab === 'residents' ? 'active' : ''}`}
                  onClick={() => setHowItWorksTab('residents')}
                >
                  For Residents
                </button>
                <button 
                  className={`tab-btn ${howItWorksTab === 'businesses' ? 'active' : ''}`}
                  onClick={() => setHowItWorksTab('businesses')}
                >
                  For Businesses
                </button>
              </div>
            </div>

            <div className="steps-grid">
              {howItWorksTab === 'residents' ? (
                <>
                  <div className="step-card">
                    <div className="step-num">01</div>
                    <h3>Download & Join</h3>
                    <p>Get the TappyTray app from Play Store or App Store and find your society.</p>
                  </div>
                  <div className="step-card">
                    <div className="step-num">02</div>
                    <h3>Verify Identity</h3>
                    <p>Securely login using your registered mobile number and OTP.</p>
                  </div>
                  <div className="step-card">
                    <div className="step-num">03</div>
                    <h3>Smart Living</h3>
                    <p>Manage guests, pay bills, and enjoy exclusive local deals instantly.</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="step-card">
                    <div className="step-num">01</div>
                    <h3>Quick Register</h3>
                    <p>Sign up your business in minutes and verify your location.</p>
                  </div>
                  <div className="step-card">
                    <div className="step-num">02</div>
                    <h3>List Your Offers</h3>
                    <p>Create beautiful digital promotions and set your target societies.</p>
                  </div>
                  <div className="step-card">
                    <div className="step-num">03</div>
                    <h3>Local Growth</h3>
                    <p>Reach thousands of nearby high-intent residents directly on their phones.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="section section-comparison reveal">
          <div className="container">
            <h2 className="section-title" style={{ textAlign: 'center' }}>The Digital Upgrade</h2>
            <p className="section-desc" style={{ textAlign: 'center', margin: '0 auto 64px auto' }}>
              Why stick to the past? See how TappyTray modernizes every aspect of society life.
            </p>
            
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Traditional Way</th>
                    <th className="highlight-column">TappyTray Way</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Security</strong></td>
                    <td>Manual Paper Registers</td>
                    <td className="highlight-cell">Instant Digital Logs</td>
                  </tr>
                  <tr>
                    <td><strong>Promotions</strong></td>
                    <td>Physical Pamphlets</td>
                    <td className="highlight-cell">Targeted Mobile Ads</td>
                  </tr>
                  <tr>
                    <td><strong>Announcements</strong></td>
                    <td>Notice Board Papers</td>
                    <td className="highlight-cell">Push Notifications</td>
                  </tr>
                  <tr>
                    <td><strong>Payments</strong></td>
                    <td>Cash & Manual Receipts</td>
                    <td className="highlight-cell">Automated Digital Billing</td>
                  </tr>
                  <tr>
                    <td><strong>Visitor Entry</strong></td>
                    <td>Intercom & Guard Shouting</td>
                    <td className="highlight-cell">Single-Tap Approval</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>


        {/* Customer Reviews */}
        <section className="section section-reviews reveal">
          <div className="container">
            <h2 className="section-title" style={{ textAlign: 'center' }}>Loved by Residents & Businesses</h2>
            <p className="section-desc" style={{ textAlign: 'center', margin: '0 auto 48px auto' }}>
              See how TappyTray is transforming society living and local business growth.
            </p>
          </div>
          
          <div className="reviews-marquee-wrapper">
            <div className="reviews-marquee">
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"Finally an app that doesn't spam me! Getting deals from the cafe downstairs right on my phone is amazing."</p>
                <h4>Priya Sharma</h4>
                <span>Resident</span>
              </div>
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"Gate management is incredibly smooth. Pre-approving guests takes seconds."</p>
                <h4>Rohan Gupta</h4>
                <span>Resident</span>
              </div>
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"We listed our local bakery and saw a 40% jump in weekend orders from nearby societies. Huge ROI!"</p>
                <h4>Anita Verma</h4>
                <span>Business Owner</span>
              </div>
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"Paying maintenance bills and checking society notices is now just one tap away. Very convenient."</p>
                <h4>Vikram Singh</h4>
                <span>Resident</span>
              </div>
              
              {/* Duplicate the set for seamless loop */}
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"Finally an app that doesn't spam me! Getting deals from the cafe downstairs right on my phone is amazing."</p>
                <h4>Priya Sharma</h4>
                <span>Resident</span>
              </div>
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"Gate management is incredibly smooth. Pre-approving guests takes seconds."</p>
                <h4>Rohan Gupta</h4>
                <span>Resident</span>
              </div>
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"We listed our local bakery and saw a 40% jump in weekend orders from nearby societies. Huge ROI!"</p>
                <h4>Anita Verma</h4>
                <span>Business Owner</span>
              </div>
              <div className="review-card">
                <div className="review-stars">★★★★★</div>
                <p>"Paying maintenance bills and checking society notices is now just one tap away. Very convenient."</p>
                <h4>Vikram Singh</h4>
                <span>Resident</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section section-faq reveal">
          <div className="container">
            <h2 className="section-title" style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>
            <div className="faq-list">
              {[
                {
                  q: "Is TappyTray free for residents?",
                  a: "Yes, the TappyTray mobile app is completely free for society residents. You can manage visitors, pay bills, and access local deals at no cost."
                },
                {
                  q: "How secure is my personal data?",
                  a: "We take security seriously. All data is encrypted and we never share your phone number with third parties without your consent. Your privacy is our priority."
                },
                {
                  q: "How can I list my business on TappyTray?",
                  a: "Simply click the 'List Your Business' button, register your account, and you can start uploading your promotions to nearby societies instantly."
                },
                {
                  q: "How do I get my society on the platform?",
                  a: "You can reach out to us via the contact section, and our team will help you onboard your society management committee."
                }
              ].map((faq, i) => (
                <div 
                  key={i} 
                  className={`faq-item ${activeFaq === i ? 'active' : ''}`}
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                >
                  <div className="faq-question">
                    <h3>{faq.q}</h3>
                    <span className="faq-toggle"></span>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Contact */}
        <section className="section section-contact reveal">
          <div className="container">
            <div className="contact-header">
              <p className="section-badge">Get in Touch</p>
              <h2 className="section-title">We'd love to hear from you</h2>
              <p className="section-desc">
                Have questions about listing your business or bringing TappyTray to your society? Reach out to us directly.
              </p>
            </div>
            <div className="contact-grid">
              <a href="mailto:info@tappytray.com" className="contact-card">
                <div className="contact-icon"><HiMail size={32} /></div>
                <h3>Email Us</h3>
                <p>info@tappytray.com</p>
              </a>
              <div className="contact-card">
                <div className="contact-icon"><HiLocationMarker size={32} /></div>
                <h3>Our Office</h3>
                <p>Flat No. 504, Shyam Heights, Sumer Nagar Ext., New Sanganer Road, Jaipur (Rajasthan)</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-left">
            <p className="footer-copy">© {new Date().getFullYear()} BoilingCode LLP. All rights reserved.</p>
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
            <Link to="/refund-policy">Refund Policy</Link>
            <Link to="/delete-account">Delete Account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
